import { FormEvent, useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  ZertifikatInput,
  createZertifikat,
  fetchZertifikate,
  fetchZertifikatsarten,
} from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { formatDatum } from "@/lib/format"
import { Button, Select } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const AMPEL_FARBEN: Record<string, string> = {
  gueltig: "bg-green-100 text-green-800",
  bald_ablaufend: "bg-orange-100 text-orange-800",
  abgelaufen: "bg-red-100 text-red-800",
}

const AMPEL_LABEL: Record<string, string> = {
  gueltig: "Gültig",
  bald_ablaufend: "Läuft bald ab",
  abgelaufen: "Abgelaufen",
}

/** Zertifikate-Sektion innerhalb der Organisation-Detailseite (Ninox `XE`). */
export function ZertifikateSection({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [artId, setArtId] = useState("")
  const [erstausstellung, setErstausstellung] = useState("")

  const { data, isLoading } = useQuery({
    queryKey: ["zertifikate", "organisation", organisationId],
    queryFn: () => fetchZertifikate(organisationId),
  })

  const { data: arten } = useQuery({
    queryKey: ["zertifikatsarten"],
    queryFn: () => fetchZertifikatsarten(),
  })

  const artenById = useMemo(() => new Map((arten ?? []).map((a) => [a.id, a.name])), [arten])

  const anlegen = useMutation({
    mutationFn: (payload: ZertifikatInput) => createZertifikat({ ...payload, organisationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zertifikate", "organisation", organisationId] })
      setArtId("")
      setErstausstellung("")
      setZeigeNeu(false)
      toast.show("Zertifikat angelegt.", "success")
    },
    onError: () => toast.show("Zertifikat konnte nicht angelegt werden.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    anlegen.mutate({
      zertifikatsartId: artId || null,
      erstausstellung: erstausstellung || null,
    })
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">Zertifikate</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + Zertifikat hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-10 w-full" />
        </div>
      )}

      <ul className="space-y-2">
        {data?.map((z) => (
          <li
            key={z.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">
                {z.zertifikatsartId ? artenById.get(z.zertifikatsartId) ?? "—" : "—"}
              </p>
              <p className="text-slate-500">Ablauf: {formatDatum(z.aktuellesAblaufdatum)}</p>
            </div>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                AMPEL_FARBEN[z.ampel ?? "gueltig"]
              }`}
            >
              {AMPEL_LABEL[z.ampel ?? "gueltig"]}
            </span>
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine Zertifikate erfasst."
          aktion={{ label: "+ Zertifikat hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="zertifikat-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neues Zertifikat</SheetTitle>
            <SheetDescription>Zertifikat für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="zertifikat-neu-form" onSubmit={onSubmitNeu} className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Zertifikatsart</label>
              <Select value={artId} onChange={(e) => setArtId(e.target.value)}>
                <option value="">— auswählen —</option>
                {arten?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-slate-700">Erstausstellung</label>
              <input
                type="date"
                value={erstausstellung}
                onChange={(e) => setErstausstellung(e.target.value)}
                className="h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/60 focus:border-slate-400"
              />
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="zertifikat-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "Zertifikat speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
